import React, { useState, useReducer, useEffect } from 'react';
import { Icon, Search, SearchResultData, SearchProps } from 'semantic-ui-react';
import { ConstituentViewModel } from '../../types/ServerDTO';
import axiosInstance from '../../helpers/axiosInstance';

type State = {
  suggestions: ConstituentViewModel[];
  isLoading: boolean;
};

type Action =
  | { type: 'FETCH_SUGGESTIONS' }
  | { type: 'SET_SUGGESTIONS'; payload: ConstituentViewModel[] }
  | { type: 'FETCH_SUGGESTIONS_FAILURE' };

const initialState: State = {
  suggestions: [],
  isLoading: false,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_SUGGESTIONS':
      return { ...state, isLoading: true };
    case 'SET_SUGGESTIONS':
      action.payload.push({
        constituentId: 0,
        constituentName: '',
        ward: '',
        neighborhood: '',
        address: '',
        email: '',
        phoneNumber: '',
        pronouns: ''
      });
      return { ...state, suggestions: action.payload, isLoading: false };
    case 'FETCH_SUGGESTIONS_FAILURE':
      return { ...state, isLoading: false };
    default:
      return state;
  }
}

interface AutocompleteProps {
  apiEndpoint: string;
  param: object,
  onHandleChange: Function,
  onHandleBlur: Function;
  defaultValue: string | undefined;
  addNewConstituent: Function;
}

const ConstituentAutoComplete: React.FC<AutocompleteProps> = ({ apiEndpoint, param, defaultValue, onHandleChange, onHandleBlur, addNewConstituent }) => {
  const [searchQuery, setSearchQuery] = useState<string | undefined>(defaultValue);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    setSearchQuery(defaultValue);
  }, [defaultValue])

  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      if (searchQuery === undefined) {
        return;
      }
      if (searchQuery && searchQuery.length < 3) {
        return;
      }

      dispatch({ type: 'FETCH_SUGGESTIONS' });

      try {
        const response = await axiosInstance.get(`${apiEndpoint}/${searchQuery}`);

        if (!didCancel) {
          dispatch({ type: 'SET_SUGGESTIONS', payload: response.data });
        }
      } catch (error) {
        if (!didCancel) {
          dispatch({ type: 'FETCH_SUGGESTIONS_FAILURE' });
        }
      }
    };

    fetchData();

    return () => {
      didCancel = true;
    };
  }, [searchQuery, apiEndpoint]);

  const onSearchChange = (e: any, { value }: SearchProps) => {
    setSearchQuery(value as string);
  };

  const onResultSelect = (e: any, { result }: SearchResultData) => {
    if (result.constituentId === 0) {
      addNewConstituent(true);
    } else {
      setSearchQuery((result as ConstituentViewModel).constituentName);
      onHandleChange((result as ConstituentViewModel));
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    onHandleChange(({} as ConstituentViewModel));
  };

  const resultRenderer = ({ address, constituentName, constituentId }: any) => {
    return (
      <>
        {constituentId === 0 && (
          <div><Icon name="plus" /> Add New Constituent</div>
        )}
        {constituentId > 0 && (
          <div key={constituentId}>
            <label>{constituentName}</label>
            <div>{address}</div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <Search
        loading={state.isLoading}
        fluid
        clearable
        name={'constituent'}
        placeholder='Search constituent...'
        onResultSelect={onResultSelect}
        onSearchChange={onSearchChange}
        onBlur={(event: any, data: SearchProps) => onHandleBlur(event)}
        results={state.suggestions}
        value={searchQuery}
        resultRenderer={resultRenderer}
        minCharacters={3}
        defaultValue={defaultValue}
        action={
          searchQuery && searchQuery.length > 3
            ? {
              icon: 'times',
              onClick: handleClearSearch,
              disabled: !searchQuery,
            }
            : undefined
        }
      />
      {searchQuery ? <Icon name="delete" onClick={handleClearSearch} className="cursor-pointer" style={{ position: 'absolute', top: '37px', right: '15px' }} /> : ''}
    </>
  );
};

export default ConstituentAutoComplete;